// ORC-41 / M1 Slice 11-12 closure — Login screen.
//
// Two-step OIDC authorization-code-with-PKCE flow:
//
//   1. Tenant entry: user types the tenant slug → SPA calls
//      POST /v1/auth/discover. On success the SPA:
//        - generates a fresh PKCE code_verifier + code_challenge,
//        - stores the verifier in sessionStorage keyed by the `state`
//          query param the API bakes into authorize_url (which carries
//          the tenant id — see HttpIdpClient.DiscoverAsync),
//        - appends `redirect_uri=${origin}/login` + `code_challenge=…`
//          + `code_challenge_method=S256` to the authorize URL,
//        - bounces the browser to the IdP via window.location.assign.
//
//   2. Callback (still /login): the IdP redirects back to
//      /login?code=…&state=… (the realm wildcards accept it on both
//      local compose + dev cluster). The callback `useEffect` below:
//        - short-circuits if AuthContext already authenticated us (a
//          stale refresh cookie can race the callback render — see
//          ORC_41_PLAN R1),
//        - consumes the matching code_verifier from sessionStorage,
//        - POSTs /v1/auth/exchange with { tenant: state, code,
//          code_verifier, redirect_uri },
//        - GETs /v1/auth/me, hydrates AuthContext via
//          loginWithTokenBundle, and navigates to the original target
//          (`from` query, falling back to "/").
//
// Local-login (`localLoginEnabled=true` without a federated IdP) keeps
// its slice-10 placeholder behaviour. The password form for that path
// is explicitly out-of-scope here (Task §4 / ORC_41_PLAN Q3).

import { useEffect, useRef, useState, type FormEvent } from "react";
import { useRouter } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { apiClient, setAccessToken } from "../api/client";
import { useI18n } from "../i18n/I18nContext";
import { useAuth, type AuthUser } from "../auth/AuthContext";
import {
  consumePkceVerifier,
  createPkcePair,
  storePkceVerifier,
} from "../auth/pkce";

const tenantSchema = z.object({
  tenant: z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9-]+$/u, "tenant identifier must be lowercase alphanumeric + dash"),
});

type TenantForm = z.infer<typeof tenantSchema>;

function redirectUri(): string {
  return `${window.location.origin}/login`;
}

export function LoginScreen(): JSX.Element {
  const { t } = useI18n();
  const router = useRouter();
  const auth = useAuth();
  const [error, setError] = useState<string | null>(null);
  // Guard against StrictMode double-mount triggering the exchange twice
  // (authorization codes are single-use; the second attempt would 4xx).
  const callbackHandledRef = useRef(false);

  const form = useForm<TenantForm>({
    defaultValues: { tenant: "demo" },
  });

  const discover = useMutation({
    mutationFn: async (tenant: string) => {
      const response = await apiClient.GET("/auth/discover", {
        params: { query: { tenant } },
      });
      if (!response.data) {
        throw new Error(response.error?.detail ?? "discover_failed");
      }
      return response.data;
    },
    onSuccess: async (discovery) => {
      // Local-login-only tenants: no federated authorize URL. Slice 13+
      // adds the password form behind a flag; for now we surface the
      // slice-10 placeholder copy.
      if (!discovery.authorize_url) {
        setError(t("login.redirect"));
        return;
      }

      let target: URL;
      try {
        target = new URL(discovery.authorize_url);
      } catch {
        setError(t("login.error.exchange_failed"));
        return;
      }

      // Append redirect_uri unless the API already supplied one
      // (forward-compatible; mirrors the reverted commit 0c9110b).
      if (!target.searchParams.has("redirect_uri")) {
        target.searchParams.set("redirect_uri", redirectUri());
      }

      // State carries the tenant id (HttpIdpClient.DiscoverAsync:41).
      // Without it the callback handler can't recover the verifier, so
      // we refuse to bounce.
      const state = target.searchParams.get("state");
      if (!state) {
        setError(t("login.error.unknown_tenant"));
        return;
      }

      // PKCE: generate + persist verifier under rc.pkce.<state>; append
      // challenge + method=S256 to the authorize URL. The local Keycloak
      // realm enforces pkce.code.challenge.method: S256, so omitting
      // this would surface as "Missing parameter: code_challenge_method"
      // and bounce back to /login?error=… (see commits 0c9110b / 16e8768).
      let pair: { codeVerifier: string; codeChallenge: string };
      try {
        pair = await createPkcePair();
      } catch {
        setError(t("login.error.unsupported_browser"));
        return;
      }
      storePkceVerifier(state, pair.codeVerifier);
      if (!target.searchParams.has("code_challenge")) {
        target.searchParams.set("code_challenge", pair.codeChallenge);
      }
      if (!target.searchParams.has("code_challenge_method")) {
        target.searchParams.set("code_challenge_method", "S256");
      }

      window.location.assign(target.toString());
    },
    onError: () => {
      setError(t("login.error.unknown_tenant"));
    },
  });

  // Read the live search string once per render so the effect's
  // dependency array is statically analysable (react-hooks/exhaustive-
  // deps doesn't accept a typeof check inline). SSR is not a concern
  // for this SPA, but the guard remains defensive.
  const search =
    typeof window === "undefined" ? "" : window.location.search;

  // Callback handler — runs whenever /login mounts with a search
  // string. Branch-by-branch:
  //   • If we have ?error=… the IdP rejected the request; clear any
  //     stashed verifier + show the IdP-error surface.
  //   • If we have ?code=&state= recover the matching verifier and
  //     POST /v1/auth/exchange, then hydrate AuthContext + navigate.
  //   • If AuthContext is already authenticated (boot-time refresh
  //     beat the callback), skip the exchange and jump to `from`.
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const params = new URLSearchParams(search);
    const code = params.get("code");
    const state = params.get("state");
    const errorParam = params.get("error");
    const from = params.get("from") ?? "/";

    if (errorParam) {
      // Drop any stashed verifier for this state so a future retry
      // starts clean. We can't navigate via the TanStack router with
      // an unknown search schema, so fall back to history.replaceState
      // to clear the query in-place.
      if (state) {
        consumePkceVerifier(state);
      }
      setError(t("login.error.idp_error"));
      window.history.replaceState({}, "", "/login");
      return;
    }

    if (!code || !state) {
      return; // not a callback render
    }

    // If a stale refresh cookie already authenticated the user before
    // our callback ran, skip the (now single-use-exhausted) exchange.
    if (auth.status === "authenticated") {
      consumePkceVerifier(state);
      void router.navigate({ to: from, replace: true });
      return;
    }

    if (callbackHandledRef.current) {
      return;
    }
    callbackHandledRef.current = true;

    const codeVerifier = consumePkceVerifier(state);
    if (!codeVerifier) {
      setError(t("login.error.session_expired"));
      window.history.replaceState({}, "", "/login");
      callbackHandledRef.current = false;
      return;
    }

    void (async () => {
      try {
        const exchange = await apiClient.POST("/auth/exchange", {
          body: {
            tenant: state,
            code,
            code_verifier: codeVerifier,
            redirect_uri: redirectUri(),
          },
        });
        if (!exchange.data) {
          throw new Error(exchange.error?.detail ?? "exchange_failed");
        }
        // Wire the freshly-minted bearer into the API client BEFORE we
        // call /auth/me — the openapi-fetch middleware reads from the
        // module-scoped accessTokenRef, which loginWithTokenBundle only
        // sets later. Mirrors AuthContext's boot-time refresh sequence.
        setAccessToken(exchange.data.access_token);
        const me = await apiClient.GET("/auth/me", {});
        if (!me.data) {
          throw new Error(me.error?.detail ?? "me_failed");
        }
        const user: AuthUser = {
          userId: me.data.user_id,
          displayName: me.data.display_name,
          email: me.data.email,
          uiHints: me.data.ui_hints ?? [],
          tenant: state,
        };
        auth.loginWithTokenBundle(
          {
            accessToken: exchange.data.access_token,
            tokenType: exchange.data.token_type,
            expiresIn: exchange.data.expires_in,
          },
          user,
        );
        // Clear ?code=&state= before navigating so a refresh on the
        // landing page does not re-trigger the callback handler.
        window.history.replaceState({}, "", "/login");
        await router.navigate({ to: from, replace: true });
      } catch {
        setError(t("login.error.exchange_failed"));
        window.history.replaceState({}, "", "/login");
        callbackHandledRef.current = false;
      }
    })();
    // We intentionally key the effect on the raw search string so that
    // navigation away + back into /login with a new code re-runs it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, auth.status]);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    void form.handleSubmit(async ({ tenant }) => {
      const parsed = tenantSchema.safeParse({ tenant });
      if (!parsed.success) {
        setError(t("login.error.unknown_tenant"));
        return;
      }
      await discover.mutateAsync(parsed.data.tenant);
      // Force route loaders to re-run in case the discover flow
      // changed any auth-derived state (kept for parity with slice-10).
      void router.invalidate();
    })(event);
  };

  return (
    <main className="rc-page">
      <div className="rc-card" style={{ maxWidth: 420, margin: "10vh auto" }}>
        <h1 style={{ marginTop: 0 }}>{t("login.title")}</h1>
        <form onSubmit={onSubmit}>
          <label htmlFor="tenant" style={{ display: "block", marginBottom: 6 }}>
            {t("login.tenant.label")}
          </label>
          <input
            id="tenant"
            className="rc-input"
            placeholder={t("login.tenant.placeholder")}
            autoComplete="organization"
            disabled={discover.isPending}
            {...form.register("tenant")}
          />
          <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
            <button
              type="submit"
              className="rc-button rc-button--primary"
              data-testid="login-sign-in"
              disabled={discover.isPending}
            >
              {discover.isPending ? t("login.discovering") : t("login.continue")}
            </button>
          </div>
          {error && (
            <p role="alert" style={{ color: "var(--rc-color-danger)", marginTop: 12 }}>
              {error}
            </p>
          )}
        </form>
      </div>
    </main>
  );
}
